import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Star } from 'lucide-react';
import { premiumWhiteCardStyle } from './allureCardStyles';

const FONT = "'Inria Serif', serif";

export const VenueRatingReminderCard = ({ prompt, onOpen, onSkip, skipping }) => {
  if (!prompt) return null;

  const remainingDays = Math.max(
    1,
    Math.ceil((prompt.remaining_seconds || 0) / (60 * 60 * 24)),
  );
  const dayWord = remainingDays === 1 ? 'day' : 'days';

  return (
    <motion.div
      data-testid="guest-home-rating-reminder-card"
      initial={{ opacity: 0, y: 14 }}
      animate={{
        opacity: 1,
        y: 0,
        boxShadow: [
          '0 0 0 0 rgba(255, 255, 255, 0.0), var(--premium-shadow-main)',
          '0 0 0 8px rgba(255, 255, 255, 0.12), var(--premium-shadow-main)',
          '0 0 0 0 rgba(255, 255, 255, 0.0), var(--premium-shadow-main)',
        ],
      }}
      transition={{
        opacity: { duration: 0.35, ease: 'easeOut' },
        y: { duration: 0.35, ease: 'easeOut' },
        boxShadow: { duration: 2.4, repeat: Infinity, ease: 'easeInOut' },
      }}
      style={{
        width: '100%',
        maxWidth: 360,
        ...premiumWhiteCardStyle,
        padding: '16px 16px 14px',
        display: 'grid',
        gap: 12,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <span
          aria-hidden
          style={{
            width: 38,
            height: 38,
            borderRadius: 12,
            background: 'rgba(244, 197, 78, 0.16)',
            color: '#D49B12',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <Star size={20} strokeWidth={2.2} fill="#F4C54E" color="#F4C54E" />
        </span>
        <div style={{ minWidth: 0, flex: 1 }}>
          <p
            data-testid="guest-home-rating-reminder-title"
            style={{
              fontFamily: FONT,
              fontSize: 19,
              fontWeight: 700,
              color: 'var(--text)',
              lineHeight: 1.15,
              margin: 0,
            }}
          >
            How was your visit at {prompt.venue_name}?
          </p>
          <p
            data-testid="guest-home-rating-reminder-message"
            style={{
              fontFamily: FONT,
              fontSize: 14,
              color: 'var(--text-2)',
              lineHeight: 1.3,
              margin: '4px 0 0',
            }}
          >
            Leave a quick rating to help other Allure guests. You have {remainingDays} {dayWord} left.
          </p>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
        <button
          type="button"
          data-testid="guest-home-rating-reminder-open-button"
          onClick={onOpen}
          style={{
            flex: 1,
            borderRadius: 9999,
            border: 'none',
            background: 'var(--solid-cta-bg)',
            color: 'var(--solid-cta-text)',
            fontFamily: FONT,
            fontSize: 15,
            fontWeight: 700,
            padding: '11px 16px',
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 6,
            boxShadow: '0 6px 14px rgba(26, 18, 38, 0.18)',
          }}
        >
          Rate now
          <ArrowRight size={15} strokeWidth={2.4} />
        </button>
        <button
          type="button"
          data-testid="guest-home-rating-reminder-skip-button"
          onClick={onSkip}
          disabled={skipping}
          style={{
            borderRadius: 9999,
            border: '1px solid var(--card-border)',
            background: 'var(--surface)',
            color: 'var(--text-2)',
            fontFamily: FONT,
            fontSize: 14,
            fontWeight: 700,
            padding: '11px 16px',
            cursor: skipping ? 'wait' : 'pointer',
            opacity: skipping ? 0.6 : 1,
          }}
        >
          {skipping ? '…' : 'Skip'}
        </button>
      </div>
    </motion.div>
  );
};
